import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../utility/apiFetch.js";

export function useTeamRosterQuery() {
  return useQuery({
    queryKey: ["team-members"],
    queryFn: () => apiFetch("teams/members"),
    staleTime: 60_000,
  });
}

export function useSendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email) =>
      apiFetch("teams/invites", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team-members"] }),
  });
}

export function useCancelInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId) =>
      apiFetch(`teams/invites/${inviteId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team-members"] }),
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uid) =>
      apiFetch(`teams/members/${uid}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team-members"] }),
  });
}

export function useUpdateTeamName() {
  return useMutation({
    mutationFn: (name) =>
      apiFetch("teams/name", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      }),
  });
}

export function useAcceptInvite() {
  return useMutation({
    mutationFn: async (token) => {
      const API = import.meta.env.VITE_API_BASE_URL;
      const raw = localStorage.getItem("fungistics-profile");
      const csrfToken = raw ? (JSON.parse(raw)._csrf ?? "") : "";
      const res = await fetch(`${API}invites/accept`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) {
        let body = {};
        try { body = await res.json(); } catch { /* ignore */ }
        const err = new Error(body.error || `invites/accept ${res.status}`);
        err.status = res.status;
        err.body = body;
        throw err;
      }
      const data = await res.json();
      const newCsrf = res.headers.get("X-CSRF-Token") ?? "";
      return { ...data, _csrf: newCsrf };
    },
    // No cache invalidation — caller handles session refresh + redirect
  });
}
